import React, { FunctionComponent, ReactElement, useRef } from 'react';

import { useGetSearchTerm } from '../../../../shared/hooks/use-get-search-term/use-get-search-term.hook';
import { APPS, APPS_FEATURE } from '../../../../store/utils/files-names';
import SearchBar from '../../../shared/components/search-bar/SearchBar';
import { useClearText } from '../../../shared/components/search-bar/hooks/use-clear-text/use-clear-text.hook';
import SetIsCheckedContextProvider from '../../../shared/components/ui/Table/components/table/context/set-is-checked/set-is-checked.context';
import Typography from '../../../shared/components/ui/Typography/Typography';
import { useInputHandlerEvent } from '../../../shared/hooks/use-input-event-handler/use-input-event-handler.hook';
import { useSearchHandlerEvent } from '../../../shared/hooks/use-search-event-handler/use-search-event-handler.hook';
import { useToggleFiltersVisibility } from '../../../shared/hooks/use-toggle-filters-visibility/use-toggle-filters-visibility.hook';
import { AppsFilters } from '../../models/apps-filters.model';
import {
  appsToggleFiltersVisibility,
  filterAppsByNameOrDescription,
} from '../../store/slices/apps.slice';
import { getApps } from '../../thunks/get-apps.thunk';
import AppsTable from '../AppsTable/AppsTable';
import AppsFilter from '../app-filters/app-filters';
import { useAreAppsFiltersVisible } from './hooks/use-are-apps-filters-visible/use-are-apps-filters-visible.hook';

const AllAppsLayout: FunctionComponent = (): ReactElement => {
  const inputRef = useRef<HTMLInputElement>(null);
  const clearText = useClearText(
    inputRef,
    filterAppsByNameOrDescription,
    'nameOrDescription'
  );
  const appsSearchTerm = useGetSearchTerm(
    `${APPS_FEATURE}.${APPS}`,
    'nameOrDescription'
  );
  const areFiltersVisible = useAreAppsFiltersVisible();
  const toggleFiltersVisibility = useToggleFiltersVisibility(
    appsToggleFiltersVisibility
  );
  const handleInputEvent = useInputHandlerEvent<AppsFilters>(
    'nameOrDescription',
    filterAppsByNameOrDescription
  );

  const handleSearchEvent = useSearchHandlerEvent(
    getApps,
    `${APPS_FEATURE}.${APPS}`
  );

  return (
    <>
      <Typography
        variant="h1"
        sx={{
          fontSize: '32px',
          fontWeight: 300,
          marginTop: '54px',
          marginBottom: '11px',
        }}
        data-testid="all-apps-layout"
      >
        Apps
      </Typography>

      <SearchBar
        searchInputPlaceholder="App name or description"
        createRoutePath="create"
        inputRef={inputRef}
        clearText={clearText}
        filterButtonClickHandler={toggleFiltersVisibility}
        searchInputChangeHandler={handleInputEvent}
        searchOnSubmitHandler={handleSearchEvent}
        areFiltersVisible={areFiltersVisible}
        initialValue={appsSearchTerm}
      />

      {areFiltersVisible && <AppsFilter />}

      <SetIsCheckedContextProvider>
        <AppsTable />
      </SetIsCheckedContextProvider>
    </>
  );
};

export default AllAppsLayout;
